import React, { useEffect, useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FileText, Plus } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PartnerApplyServiceFlow, {
  PARTNER_SERVICE_CATALOG,
} from '../../components/partner/PartnerApplyServiceFlow';

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-yellow-100 text-yellow-700' },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-700' },
  WAITING_STUDENT: { label: 'Waiting', color: 'bg-orange-100 text-orange-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  ON_HOLD: { label: 'On Hold', color: 'bg-gray-100 text-gray-700' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

export default function PartnerServiceRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [page, statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const response = await partnerAPI.getServiceRequests(params);
      setRequests(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch service requests');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => setCreateOpen(true);

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Service Requests</h1>
            <p className="mt-1 text-slate-600">Manage service requests for your students</p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="partner-primary-btn rounded-2xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setStatusFilter(''); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              !statusFilter ? 'partner-chip partner-chip-active' : 'partner-chip'
            }`}
          >
            All
          </button>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <button
              key={key}
              onClick={() => { setStatusFilter(key); setPage(1); }}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                statusFilter === key ? 'partner-chip partner-chip-active' : 'partner-chip'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <Card className="partner-card rounded-[28px] p-12 text-center">
            <FileText className="mx-auto mb-3 h-12 w-12 text-[#A87DFA]" />
            <h3 className="text-lg font-semibold text-slate-900">No service requests</h3>
            <p className="mb-4 mt-1 text-sm text-slate-600">Create a service request for one of your students</p>
            <Button onClick={openCreateDialog} className="partner-primary-btn rounded-2xl">
              <Plus className="w-4 h-4 mr-2" /> New Request
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => {
              const statusConf = STATUS_CONFIG[req.status] || STATUS_CONFIG.PENDING_ADMIN_ASSIGNMENT;
              const serviceLabel = PARTNER_SERVICE_CATALOG.find(s => s.serviceType === req.serviceType)?.label || req.serviceType;
              return (
                <Card key={req.serviceRequestId} className="partner-card partner-card-hover cursor-pointer rounded-[26px] p-4"
                  onClick={() => navigate(`/partner/service-requests/${req.serviceRequestId}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-slate-900">{serviceLabel}</h3>
                        <Badge className={statusConf.color}>{statusConf.label}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        {req.student?.user && (
                          <span>Student: {req.student.user.firstName} {req.student.user.lastName}</span>
                        )}
                        {req.counselor && (
                          <span>Counselor: {req.counselor.firstName} {req.counselor.lastName}</span>
                        )}
                        <span>Progress: {req.progress || 0}%</span>
                      </div>
                      <div className="mt-2 h-1.5 w-full rounded-full bg-[#A87DFA]/10">
                        <div
                          className="h-1.5 rounded-full bg-[linear-gradient(90deg,#A87DFA,#8E66F1)] transition-all"
                          style={{ width: `${req.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">Page {page} of {pagination.totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <PartnerApplyServiceFlow
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={fetchRequests}
      />
    </PartnerLayout>
  );
}
